// src/pages/RegisterCliente.jsx
import React, { useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  Stack,
  FormControlLabel,
  Checkbox,
  Snackbar,
  LinearProgress,
  Fade,
  Divider,
  Chip,
  Link,
  Avatar,
  alpha,
  useTheme,
} from "@mui/material";

import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LockIcon from "@mui/icons-material/Lock";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import StorefrontIcon from "@mui/icons-material/Storefront";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import BuildCircleRoundedIcon from "@mui/icons-material/BuildCircleRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";

import { useAuth } from "../contexts/AuthContext";

export default function RegisterCliente() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const colors = {
    accent: theme.palette.primary.main,
    text: theme.palette.text.primary,
    muted: theme.palette.text.secondary,
    border: isDark ? "rgba(148,163,184,0.16)" : "rgba(15,23,42,0.08)",
    surface: isDark ? "rgba(15,23,42,0.78)" : "rgba(255,255,255,0.78)",
    surfaceStrong: isDark ? "rgba(15,23,42,0.94)" : "rgba(255,255,255,0.96)",
    hero: isDark ? "rgba(2,6,23,0.96)" : "rgba(15,23,42,0.96)",
  };

  const { registerClient } = useAuth();
  const navigate = useNavigate();

  const [nome, setNome] = useState("");
  const [empresa, setEmpresa] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [terms, setTerms] = useState(false);
  const [error, setError] = useState("");
  const [openSnack, setOpenSnack] = useState(false);
  const [loading, setLoading] = useState(false);

  const toggleShowPassword = () => setShowPassword((v) => !v);

  const isValidEmail = (emailStr) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(emailStr).toLowerCase());

  const validateForm = () => {
    if (!nome.trim()) return "Por favor indica o teu nome.";
    if (!isValidEmail(email)) return "Email inválido.";
    if (!phone.trim()) return "Por favor indica o teu telefone.";
    if (password.length < 6)
      return "A palavra-passe deve ter pelo menos 6 caracteres.";
    if (password !== confirm) return "As palavras-passe não coincidem.";
    if (!terms) return "Aceita os termos de utilização para continuares.";
    return null;
  };

  const passwordStrength = () => {
    let score = 0;
    if (password.length >= 6) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/\d/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;
    return (score / 4) * 100;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const vErr = validateForm();
    if (vErr) {
      setError(vErr);
      setOpenSnack(true);
      return;
    }

    setLoading(true);
    try {
      await registerClient(
        {
          nome: nome.trim(),
          empresa: empresa.trim() || null,
          phone: phone.trim() || null,
          email: email.trim().toLowerCase(),
        },
        password
      );

      navigate("/");
    } catch (err) {
      const msg =
        err && err.message ? err.message : "Erro ao registar. Tenta novamente.";
      setError(msg);
      setOpenSnack(true);
    } finally {
      setLoading(false);
    }
  };

  const strength = passwordStrength();
  const strengthColor = strength < 40 ? "error" : strength < 75 ? "warning" : "success";
  const strengthLabel = strength < 40 ? "fraca" : strength < 75 ? "média" : "forte";

  const benefits = [
    {
      icon: <BuildCircleRoundedIcon sx={{ fontSize: 20 }} />,
      title: "Acesso a serviços",
      text: "Consulta reparações, manutenção e outros serviços disponíveis.",
    },
    {
      icon: <CalendarMonthRoundedIcon sx={{ fontSize: 20 }} />,
      title: "Reservas simplificadas",
      text: "Marca assistência técnica e escolhe o horário com facilidade.",
    },
    {
      icon: <ShieldRoundedIcon sx={{ fontSize: 20 }} />,
      title: "Acompanhamento seguro",
      text: "Segue o estado do teu pedido ou reparação na tua conta.",
    },
  ];

  const trustItems = [
    { icon: <VerifiedUserRoundedIcon />, label: "Conta cliente" },
    { icon: <SpeedRoundedIcon />, label: "Registo rápido" },
    { icon: <AutoAwesomeRoundedIcon />, label: "Área moderna" },
  ];

  const inputStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "20px",
      bgcolor: colors.surfaceStrong,
      backdropFilter: "blur(12px)",
      transition: "all 0.22s ease",
      boxShadow: isDark
        ? "0 14px 34px rgba(0,0,0,0.18)"
        : "0 14px 34px rgba(15,23,42,0.06)",
      "& fieldset": {
        borderColor: colors.border,
      },
      "&:hover": {
        transform: "translateY(-1px)",
        boxShadow: isDark
          ? "0 18px 40px rgba(0,0,0,0.24)"
          : "0 18px 40px rgba(15,23,42,0.09)",
      },
      "&:hover fieldset": {
        borderColor: alpha(colors.accent, 0.42),
      },
      "&.Mui-focused": {
        boxShadow: `0 0 0 4px ${alpha(colors.accent, 0.12)}, 0 18px 42px rgba(15,23,42,0.10)`,
      },
      "&.Mui-focused fieldset": {
        borderColor: colors.accent,
        borderWidth: 1,
      },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: colors.accent,
    },
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        display: "grid",
        placeItems: "center",
        px: { xs: 1.5, sm: 2.5, md: 4 },
        py: { xs: 2.5, md: 5 },
        overflow: "hidden",
        background: isDark
          ? "linear-gradient(135deg, #020617 0%, #0f172a 52%, #111827 100%)"
          : "linear-gradient(135deg, #f8fafc 0%, #eef2ff 48%, #f8fafc 100%)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background: isDark
            ? `radial-gradient(900px 460px at 10% 5%, ${alpha(colors.accent, 0.2)}, transparent 62%), radial-gradient(760px 440px at 92% 12%, rgba(20,184,166,0.12), transparent 58%), radial-gradient(760px 520px at 50% 100%, rgba(59,130,246,0.10), transparent 60%)`
            : `radial-gradient(900px 460px at 10% 5%, ${alpha(colors.accent, 0.18)}, transparent 62%), radial-gradient(760px 440px at 92% 12%, rgba(20,184,166,0.14), transparent 58%), radial-gradient(760px 520px at 50% 100%, rgba(59,130,246,0.10), transparent 60%)`,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: isDark ? 0.14 : 0.3,
          pointerEvents: "none",
          backgroundImage:
            "linear-gradient(rgba(148,163,184,0.16) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.16) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          maskImage: "linear-gradient(to bottom, black, transparent 78%)",
        }}
      />

      <Paper
        elevation={0}
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: 1240,
          overflow: "hidden",
          borderRadius: { xs: "30px", md: "46px" },
          border: `1px solid ${isDark ? "rgba(148,163,184,0.18)" : "rgba(255,255,255,0.82)"}`,
          bgcolor: colors.surface,
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
          boxShadow: isDark
            ? "0 34px 110px rgba(0,0,0,0.46), inset 0 1px 0 rgba(255,255,255,0.06)"
            : "0 34px 110px rgba(15,23,42,0.14), inset 0 1px 0 rgba(255,255,255,0.85)",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.08fr 0.92fr" },
            minHeight: { md: 820 },
          }}
        >
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              px: { xs: 3, sm: 4, md: 6.5 },
              py: { xs: 4, md: 5.5 },
              color: "common.white",
              bgcolor: colors.hero,
              display: { xs: "none", md: "flex" },
              flexDirection: "column",
              justifyContent: "space-between",
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                background: `
                  radial-gradient(620px 320px at 16% 8%, ${alpha(colors.accent, 0.5)}, transparent 62%),
                  radial-gradient(520px 300px at 90% 18%, rgba(20,184,166,0.28), transparent 60%),
                  radial-gradient(680px 360px at 46% 100%, rgba(59,130,246,0.22), transparent 64%)
                `,
                pointerEvents: "none",
              },
              "&::after": {
                content: '""',
                position: "absolute",
                width: 320,
                height: 320,
                borderRadius: "50%",
                right: -120,
                bottom: -120,
                background: "rgba(255,255,255,0.08)",
                filter: "blur(2px)",
              },
            }}
          >
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar
                  sx={{
                    width: 62,
                    height: 62,
                    borderRadius: "22px",
                    background: "rgba(255,255,255,0.14)",
                    border: "1px solid rgba(255,255,255,0.22)",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.18), 0 18px 36px rgba(0,0,0,0.18)",
                  }}
                >
                  <StorefrontIcon />
                </Avatar>

                <Box>
                  <Typography fontWeight={950} fontSize="1.08rem">
                    Sintanet
                  </Typography>
                  <Typography sx={{ opacity: 0.76, fontSize: "0.92rem" }}>
                    Conta de cliente
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 5 }}>
                {trustItems.map((item) => (
                  <Chip
                    key={item.label}
                    icon={item.icon}
                    label={item.label}
                    size="small"
                    sx={{
                      height: 32,
                      borderRadius: "999px",
                      bgcolor: "rgba(255,255,255,0.10)",
                      color: "#ffffff",
                      border: "1px solid rgba(255,255,255,0.16)",
                      fontWeight: 850,
                      "& .MuiChip-icon": { color: "inherit" },
                    }}
                  />
                ))}
              </Stack>

              <Typography
                sx={{
                  mt: 3,
                  fontWeight: 950,
                  lineHeight: 0.98,
                  fontSize: { xs: "2.35rem", sm: "3rem", md: "4.05rem" },
                  maxWidth: 620,
                  letterSpacing: "-0.075em",
                  textShadow: "0 22px 54px rgba(0,0,0,0.24)",
                }}
              >
                Cria a tua conta e gere tudo num só lugar.
              </Typography>

              <Typography
                sx={{
                  mt: 2.5,
                  maxWidth: 560,
                  opacity: 0.76,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  lineHeight: 1.82,
                }}
              >
                Regista-te para aceder à área de cliente, fazer reservas e acompanhar pedidos ou reparações de forma simples e segura.
              </Typography>

              <Stack spacing={1.55} sx={{ mt: 4 }}>
                {[
                  "Ver serviços disponíveis para assistência e manutenção",
                  "Efetuar reservas com rapidez e escolher o melhor horário",
                  "Acompanhar o estado do pedido ou reparação",
                ].map((item) => (
                  <Stack key={item} direction="row" spacing={1.2} alignItems="center">
                    <CheckCircleRoundedIcon sx={{ fontSize: 21, opacity: 0.96 }} />
                    <Typography sx={{ opacity: 0.92 }}>{item}</Typography>
                  </Stack>
                ))}
              </Stack>

              <Typography variant="caption" sx={{ mt: 3, opacity: 0.78, display: "block", lineHeight: 1.6 }}>
                Esta conta é exclusiva para clientes e não dá acesso ao painel interno de gestão.
              </Typography>
            </Box>

            <Stack spacing={1.35} sx={{ mt: { xs: 5, md: 6 }, position: "relative", zIndex: 1 }}>
              {benefits.map((item) => (
                <Box
                  key={item.title}
                  sx={{
                    p: 2.25,
                    borderRadius: "24px",
                    background: "rgba(255,255,255,0.10)",
                    border: "1px solid rgba(255,255,255,0.16)",
                    backdropFilter: "blur(14px)",
                    transition: "all 0.22s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      background: "rgba(255,255,255,0.14)",
                    },
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Avatar
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: "16px",
                        background: "rgba(255,255,255,0.13)",
                        border: "1px solid rgba(255,255,255,0.12)",
                        color: "#ffffff",
                        flexShrink: 0,
                      }}
                    >
                      {item.icon}
                    </Avatar>

                    <Box>
                      <Typography fontWeight={950}>{item.title}</Typography>
                      <Typography sx={{ opacity: 0.72, fontSize: "0.92rem", mt: 0.35, lineHeight: 1.6 }}>
                        {item.text}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>

          <Box
            sx={{
              p: { xs: 3, sm: 4, md: 6 },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: isDark
                ? "rgba(15,23,42,0.72)"
                : "linear-gradient(180deg, rgba(255,255,255,0.70) 0%, rgba(255,255,255,0.98) 100%)",
            }}
          >
            <Box sx={{ width: "100%", maxWidth: 470 }}>
              <Stack spacing={3}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
                  <Box>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                      <Avatar
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: "22px",
                          bgcolor: alpha(colors.accent, isDark ? 0.16 : 0.08),
                          border: `1px solid ${alpha(colors.accent, 0.14)}`,
                          boxShadow: `0 18px 38px ${alpha(colors.accent, 0.13)}`,
                          color: colors.accent,
                        }}
                      >
                        <PersonIcon />
                      </Avatar>

                      <Box>
                        <Typography
                          variant="h4"
                          fontWeight={950}
                          sx={{
                            letterSpacing: "-0.055em",
                            fontSize: { xs: "1.85rem", md: "2.25rem" },
                            color: colors.text,
                          }}
                        >
                          Criar conta
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Registo destinado a clientes.
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>

                  <Button
                    component={RouterLink}
                    to="/"
                    startIcon={<ArrowBackRoundedIcon />}
                    sx={{
                      textTransform: "none",
                      borderRadius: "999px",
                      px: 1.5,
                      whiteSpace: "nowrap",
                      alignSelf: "flex-start",
                      fontWeight: 900,
                    }}
                  >
                    Voltar
                  </Button>
                </Stack>

                <Chip
                  label="CONTA CLIENTE"
                  size="small"
                  sx={{
                    width: "fit-content",
                    borderRadius: "999px",
                    fontWeight: 900,
                    letterSpacing: 0.4,
                    bgcolor: alpha(colors.accent, isDark ? 0.16 : 0.08),
                    color: colors.accent,
                  }}
                />

                {error && (
                  <Fade in={!!error}>
                    <Alert severity="error" onClose={() => setError("")} sx={{ borderRadius: "18px" }}>
                      {error}
                    </Alert>
                  </Fade>
                )}

                <Divider sx={{ borderColor: colors.border }} />

                <Box component="form" onSubmit={handleSubmit} noValidate>
                  <Stack spacing={1.65}>
                    <TextField
                      label="Nome completo"
                      fullWidth
                      required
                      value={nome}
                      onChange={(e) => setNome(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={inputStyles}
                    />

                    <TextField
                      label="Empresa (opcional)"
                      fullWidth
                      value={empresa}
                      onChange={(e) => setEmpresa(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ApartmentRoundedIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={inputStyles}
                    />

                    <TextField
                      label="Telefone"
                      fullWidth
                      required
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={inputStyles}
                    />

                    <TextField
                      label="Email"
                      type="email"
                      fullWidth
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      error={email.length > 0 && !isValidEmail(email)}
                      helperText={email.length > 0 && !isValidEmail(email) ? "Email inválido" : " "}
                      sx={inputStyles}
                    />

                    <TextField
                      label="Palavra-passe"
                      type={showPassword ? "text" : "password"}
                      fullWidth
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={toggleShowPassword}
                              edge="end"
                              aria-label={showPassword ? "Ocultar password" : "Mostrar password"}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      error={password.length > 0 && password.length < 6}
                      helperText={password.length > 0 && password.length < 6 ? "Mínimo 6 caracteres" : " "}
                      sx={inputStyles}
                    />

                    {password && (
                      <Fade in>
                        <Box
                          sx={{
                            mt: -0.5,
                            p: 1.65,
                            borderRadius: "20px",
                            bgcolor: alpha(colors.accent, isDark ? 0.1 : 0.045),
                            border: `1px solid ${alpha(colors.accent, isDark ? 0.16 : 0.08)}`,
                          }}
                        >
                          <LinearProgress
                            variant="determinate"
                            value={strength}
                            color={strengthColor}
                            sx={{
                              borderRadius: "999px",
                              height: 8,
                              bgcolor: isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.06)",
                            }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                            Força da palavra-passe: <b>{strengthLabel}</b>
                          </Typography>
                        </Box>
                      </Fade>
                    )}

                    <TextField
                      label="Confirmar palavra-passe"
                      type={showPassword ? "text" : "password"}
                      fullWidth
                      required
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      error={confirm.length > 0 && confirm !== password}
                      helperText={confirm.length > 0 && confirm !== password ? "As palavras-passe não coincidem" : " "}
                      sx={inputStyles}
                    />

                    <Box
                      sx={{
                        p: 2.2,
                        borderRadius: "22px",
                        bgcolor: alpha(colors.accent, isDark ? 0.1 : 0.045),
                        border: `1px solid ${alpha(colors.accent, isDark ? 0.16 : 0.08)}`,
                      }}
                    >
                      <FormControlLabel
                        sx={{ alignItems: "flex-start", m: 0 }}
                        control={
                          <Checkbox
                            checked={terms}
                            onChange={(e) => setTerms(e.target.checked)}
                            sx={{ pt: 0.2 }}
                          />
                        }
                        label={
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                            Aceito os{" "}
                            <Link
                              component={RouterLink}
                              to="/termos-condicoes"
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              sx={{ fontWeight: 800 }}
                            >
                              Termos & Condições
                            </Link>{" "}
                            e a{" "}
                            <Link
                              component={RouterLink}
                              to="/politica-privacidade"
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              sx={{ fontWeight: 800 }}
                            >
                              Política de Privacidade
                            </Link>
                            .
                          </Typography>
                        }
                      />
                    </Box>

                    <Button
                      type="submit"
                      size="large"
                      fullWidth
                      disabled={loading}
                      endIcon={!loading ? <ArrowForwardRoundedIcon /> : undefined}
                      sx={{
                        mt: 0.75,
                        py: 1.65,
                        borderRadius: "20px",
                        textTransform: "none",
                        fontSize: "1rem",
                        fontWeight: 950,
                        color: "common.white",
                        background: `linear-gradient(90deg, ${colors.accent} 0%, #2563eb 55%, #0f766e 100%)`,
                        boxShadow: "0 20px 42px rgba(37,99,235,0.30)",
                        transition: "all 0.22s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 26px 54px rgba(37,99,235,0.36)",
                          filter: "saturate(1.08)",
                        },
                        "&.Mui-disabled": {
                          color: "rgba(255,255,255,0.82)",
                          background: isDark
                            ? "linear-gradient(90deg, #334155, #475569)"
                            : "linear-gradient(90deg, #a5b4fc, #93c5fd)",
                          boxShadow: "none",
                        },
                      }}
                    >
                      {loading ? "A registar..." : "Criar conta de cliente"}
                    </Button>

                    <Box
                      sx={{
                        p: 2.3,
                        borderRadius: "22px",
                        bgcolor: alpha(colors.accent, isDark ? 0.12 : 0.045),
                        border: `1px solid ${alpha(colors.accent, isDark ? 0.18 : 0.1)}`,
                      }}
                    >
                      <Typography variant="body2" textAlign="center" color="text.secondary">
                        Já tens conta?{" "}
                        <RouterLink to="/login" style={{ textDecoration: "none" }}>
                          <Typography component="span" variant="body2" color="primary" sx={{ fontWeight: 950 }}>
                            Entrar
                          </Typography>
                        </RouterLink>
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                <Snackbar
                  open={openSnack}
                  autoHideDuration={6000}
                  onClose={() => setOpenSnack(false)}
                  anchorOrigin={{ vertical: "top", horizontal: "center" }}
                >
                  {error ? (
                    <Alert severity="error" onClose={() => setOpenSnack(false)} sx={{ borderRadius: "18px" }}>
                      {error}
                    </Alert>
                  ) : null}
                </Snackbar>
              </Stack>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
